import { useEffect, useState } from "react";
import "./PlayersCountField.css";

/**
 * Players-count control for the court checkout, shared by
 * `ClubDetailDesktop.jsx` and `ClubDetailMobile.jsx`.
 *
 * The count is ALWAYS part of the reservation (it is stored as the
 * reservation's `partySize`, seeded from the sport's default). The checkbox
 * does not decide whether the number exists — it decides whether the buyer
 * takes ownership of it:
 *
 *   unchecked -> the field is read-only and keeps tracking the sport default,
 *                so switching from a padel court to a football 5 one re-seeds
 *                4 -> 10 without the buyer doing anything.
 *   checked   -> the field is editable and stops following the default. We
 *                never overwrite a number someone typed.
 *
 * Typing is kept in local string state so the input can pass through the empty
 * string and other half-typed values while the buyer edits. Only a valid
 * in-range integer is pushed up to `onChange`; leaving the field while it holds
 * an invalid value snaps the text back to the last accepted count, so the
 * committed state can never disagree with what is on screen.
 */
export default function PlayersCountField({
  value,
  onChange,
  edited,
  onEditedChange,
  defaultPlayers,
  max,
  sportLabel,
  min = 1,
}) {
  const [draft, setDraft] = useState(String(value ?? ""));

  // Re-sync the visible text whenever the committed count moves for a reason
  // other than typing here — the sport default changing, or a re-mount.
  useEffect(() => {
    setDraft(String(value ?? ""));
  }, [value]);

  const clamp = (n) => Math.min(Math.max(n, min), max);

  const commit = (raw) => {
    const parsed = Number(raw);
    if (raw === "" || !Number.isInteger(parsed)) return false;
    if (parsed < min || parsed > max) return false;
    onChange(parsed);
    return true;
  };

  const handleToggle = (checked) => {
    onEditedChange(checked);
    // Unchecking hands the number back to the sport default, which is the only
    // thing "no lo edito yo" can honestly mean.
    if (!checked) onChange(defaultPlayers);
  };

  const step = (delta) => {
    const next = clamp((Number(value) || defaultPlayers) + delta);
    onChange(next);
  };

  const draftNumber = Number(draft);
  const showError =
    edited &&
    (draft === "" ||
      !Number.isInteger(draftNumber) ||
      draftNumber < min ||
      draftNumber > max);

  return (
    <div className="players-field">
      <label className="players-field__toggle">
        <input
          type="checkbox"
          checked={edited}
          onChange={(e) => handleToggle(e.target.checked)}
        />
        Quiero indicar cuántos jugadores van a asistir
      </label>

      <div className="players-field__control">
        <button
          type="button"
          className="players-field__step"
          onClick={() => step(-1)}
          disabled={!edited || Number(value) <= min}
          aria-label="Quitar un jugador"
        >
          −
        </button>
        <input
          className="players-field__input"
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          value={draft}
          disabled={!edited}
          aria-label="Cantidad de jugadores"
          onChange={(e) => {
            const raw = e.target.value;
            setDraft(raw);
            commit(raw);
          }}
          onBlur={() => {
            // Snap back rather than silently committing garbage: the count is
            // persisted, so the screen must show exactly what will be stored.
            if (!commit(draft)) setDraft(String(value ?? defaultPlayers));
          }}
        />
        <button
          type="button"
          className="players-field__step"
          onClick={() => step(1)}
          disabled={!edited || Number(value) >= max}
          aria-label="Agregar un jugador"
        >
          +
        </button>
        <span className="players-field__unit">
          {Number(value) === 1 ? "jugador" : "jugadores"}
        </span>
      </div>

      {showError ? (
        <span className="players-field__error">
          Ingresá un número entre {min} y {max}.
        </span>
      ) : (
        <span className="players-field__hint">
          {edited
            ? "Usamos este número para calcular cuánto paga cada uno."
            : `Por defecto${sportLabel ? ` para ${sportLabel}` : ""}: ${defaultPlayers} jugadores. Marcá la casilla para cambiarlo.`}
        </span>
      )}
    </div>
  );
}
